import { type Control, useWatch } from 'react-hook-form';
import { FormattedDate } from 'react-intl';

import type { EstimationAdditionalFieldDefinitionResponse } from '@/api/schema/data-contracts';
import { ApiEstimationAdditionalFieldType } from '@/api/schema/data-contracts';
import { DatePickerFormField } from '@/components/atoms/DatePickerFormField';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { DurationFormField } from '@/components/atoms/DurationFormField';
import { fieldErrorMessageDefinitions } from '@/components/atoms/FieldErrors/messages';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { TextFormField } from '@/components/atoms/TextFormField';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { MetadataMessages } from '@/components/organism/NewEstimation/Metadata/messages';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';
import { isHttpUrl } from '@/utils/url';

import type { AdditionalFieldsFormModel } from './model';

export interface AdditionalFieldProps {
  control: Control<AdditionalFieldsFormModel>;
  fieldDefinition: EstimationAdditionalFieldDefinitionResponse;
  isReadOnly?: boolean;
  onBlur?: () => void;
}

export const AdditionalField: FC<AdditionalFieldProps> = ({
  control,
  fieldDefinition,
  isReadOnly,
  onBlur,
}) => {
  const formModel = useWatch({ control });
  const messages = useMessages(MetadataMessages);
  const fieldErrorMessages = useMessages(fieldErrorMessageDefinitions);

  if (!formModel.fields) {
    console.error('Form model fields cannot be undefined');
    return null;
  }

  const formModelFieldIndex = formModel.fields.findIndex(
    (formField) => formField.estimationModelAdditionalFieldDefinitionId === fieldDefinition.id,
  );
  if (isReadOnly) {
    if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.FORMATTED_TEXT) {
      return (
        <SanitizedHtml
          htmlString={
            (formModel.fields[formModelFieldIndex].value as string | null) ??
            `! ${messages.emptyValueLabel()}`
          }
        />
      );
    }
    if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DATE) {
      return (
        <div>
          {formModel.fields[formModelFieldIndex].value ? (
            <FormattedDate value={formModel.fields[formModelFieldIndex].value} />
          ) : (
            `! ${messages.emptyValueLabel()}`
          )}
        </div>
      );
    }
    if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DOCUMENT_LINK) {
      const linkValue =
        (formModel.fields[formModelFieldIndex].value as string | null)?.trim() ?? '';
      if (!linkValue) {
        return <div>{`! ${messages.emptyValueLabel()}`}</div>;
      }
      return isHttpUrl(linkValue) ? (
        <a
          href={linkValue}
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-400 underline hover:text-blue-300'
        >
          {linkValue}
        </a>
      ) : (
        <div>{linkValue}</div>
      );
    }
    return (
      <div>
        {(formModel.fields[formModelFieldIndex].value as string | null) ??
          `! ${messages.emptyValueLabel()}`}
      </div>
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.TEXT) {
    return (
      <TextFormField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{
          required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false,
          maxLength: entityFieldSettings.estimationAdditionalFieldEntity.valueMaxLength,
        }}
        onBlur={onBlur}
      />
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.FORMATTED_TEXT) {
    return (
      <WysiwygFormTextField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{
          required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false,
          maxLength: entityFieldSettings.estimationAdditionalFieldEntity.valueMaxLength,
        }}
        onBlur={onBlur}
      />
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DURATION_TIME_SPAN) {
    return (
      <DurationFormField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{
          required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false,
          maxLength: entityFieldSettings.estimationAdditionalFieldEntity.valueMaxLength,
        }}
        onBlur={onBlur}
      />
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.SELECTABLE) {
    const options = fieldDefinition.selectableOptions.map((option) => ({
      label: option,
      value: option,
    }));
    return (
      <DropdownFormField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{ required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false }}
        onChange={onBlur}
        options={options}
        fullWidth
      />
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DATE) {
    return (
      <DatePickerFormField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{ required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false }}
        onChange={onBlur}
      />
    );
  }
  if (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DOCUMENT_LINK) {
    return (
      <TextFormField
        control={control}
        fieldPath={`fields.${formModelFieldIndex}.value`}
        rules={{
          maxLength: entityFieldSettings.estimationAdditionalFieldEntity.valueMaxLength,
          required: fieldDefinition.isRequired ? fieldErrorMessages.required() : false,
          validate: (value) => {
            if (!value) {
              return true;
            }
            return isHttpUrl(String(value)) || messages.invalidUrl();
          },
        }}
        onBlur={onBlur}
      />
    );
  }
};
